import type {
  ResultsRuntimeSnapshot,
  RoundResultModel,
} from "./results-runtime";
import { createShareCard, type ShareCardModel } from "./share-card";
import type { ShareBoundaryResult, SharePayload } from "../config/share-config";

export type ShareRuntimeStatus =
  | "cancelled"
  | "failed"
  | "inactive"
  | "ready"
  | "shared"
  | "sharing"
  | "unavailable";

export interface ShareRuntimeSnapshot {
  readonly card: ShareCardModel | null;
  readonly ready: boolean;
  readonly status: ShareRuntimeStatus;
}

export interface ShareResultsPort {
  subscribe(listener: (snapshot: ResultsRuntimeSnapshot) => void): () => void;
}

export interface SharePort {
  share(payload: SharePayload): Promise<ShareBoundaryResult>;
}

const cloneCard = (card: ShareCardModel): ShareCardModel => ({
  ...card,
  share: { ...card.share, image: { ...card.share.image } },
});

const cardForResult = (result: RoundResultModel): ShareCardModel =>
  createShareCard({
    ...(result.challenge === undefined
      ? {}
      : {
          challenge: {
            displayName: result.challenge.shareDisplayName,
            identity: result.challenge.identity,
            kind: result.challenge.kind,
            shareCallToAction: result.challenge.shareCallToAction,
          },
        }),
    finalLevel: result.finalLevel,
    isNewBestLevel: result.isNewBestLevel,
    mode: result.mode,
    selectedSkinId: result.selectedSkinId,
  });

export class ShareRuntime {
  private card: ShareCardModel | null = null;
  private generation = 0;
  private readonly listeners = new Set<
    (snapshot: ShareRuntimeSnapshot) => void
  >();
  private ready = false;
  private resultId: string | null = null;
  private status: ShareRuntimeStatus = "inactive";

  public constructor(
    results: ShareResultsPort,
    private readonly boundary: SharePort,
  ) {
    results.subscribe((snapshot) => {
      this.applyResults(snapshot);
    });
  }

  public getSnapshot(): ShareRuntimeSnapshot {
    return {
      card: this.card === null ? null : cloneCard(this.card),
      ready: this.ready,
      status: this.status,
    };
  }

  public share(): boolean {
    const card = this.card;
    if (card === null || !this.ready || this.status === "sharing") {
      return false;
    }
    const generation = this.generation;
    this.status = "sharing";
    this.notify();
    void this.boundary.share(card.share).then((status) => {
      if (this.generation !== generation) {
        return;
      }
      this.status = status;
      this.notify();
    });
    return true;
  }

  public subscribe(
    listener: (snapshot: ShareRuntimeSnapshot) => void,
  ): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private applyResults(snapshot: ResultsRuntimeSnapshot): void {
    const result = snapshot.result;
    if (result === null) {
      if (this.resultId !== null) {
        this.generation += 1;
      }
      this.card = null;
      this.ready = false;
      this.resultId = null;
      this.status = "inactive";
      this.notify();
      return;
    }
    if (result.id !== this.resultId) {
      this.generation += 1;
      this.card = cardForResult(result);
      this.resultId = result.id;
      this.status = "inactive";
    }
    this.ready = snapshot.navigationAvailable;
    if (this.ready && this.status === "inactive") {
      this.status = "ready";
    }
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.getSnapshot());
    }
  }
}
